// Invoice Data Types and Interfaces
// Phase 2 - Week 5: Invoice Generation Migration
// Based on existing 7twenty4gen2 invoice system structure

export interface Client {
  id: string;
  name: string;
  code: string; // e.g., 'CEC', 'RELIABLE', 'BTC'
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  contactInfo: {
    email: string;
    phone: string;
    contactPerson: string;
  };
  billingInfo: {
    paymentTerms: number; // days
    taxRate: number; // percentage
    preferredFormat: 'pdf' | 'email' | 'both';
  };
  status: 'active' | 'inactive';
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface Job {
  id: string;
  jobNumber: string;
  jobName: string;
  jobDescription?: string;
  clientId: string;
  clientName: string;
  status: 'active' | 'completed' | 'cancelled';
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface Activity {
  id: string;
  activityCode: string;
  activityDescription: string;
  isActive: boolean;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface InvoiceLineItem {
  id: string;
  employeeName: string;
  activityDescription?: string;
  
  // Hours breakdown
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  
  // Rates
  regularRate: number;
  overtimeRate: number;
  
  // Calculations
  regularTotal: number;
  overtimeTotal: number;
  lineTotal: number;
  
  // Expense flag
  isExpense: boolean;
  expenseDescription?: string;
  expenseAmount?: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  jobId: string;
  jobNumber: string;
  jobName: string;
  
  // Client information
  clientId?: string;
  clientName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZip: string;
  
  // Contractor information
  contractorId?: string;
  contractorName?: string;
  
  // Date information
  invoiceDate: Date;
  dueDate: Date;
  weekEnding: string; // Can be date or date range
  periodStart?: Date;
  periodEnd?: Date;
  
  // Line items and calculations
  lineItems: InvoiceLineItem[];
  
  // Totals
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalHours: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  totalExpenses: number;
  
  // Status and workflow
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  
  // Prefixes for different clients
  useBtcPrefix: boolean;
  useRelPrefix: boolean;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  
  // Related timesheet IDs
  timesheetIds: string[];
}

export interface InvoiceFormData {
  jobId: string;
  jobNumber: string;
  jobName: string;
  clientName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZip: string;
  invoiceDate: string; // YYYY-MM-DD format
  weekEnding: string;
  periodStart?: string;
  periodEnd?: string;
  useBtcPrefix: boolean;
  useRelPrefix: boolean;
  lineItems: InvoiceLineItem[];
}

export interface InvoiceFilters {
  invoiceNumber?: string;
  jobNumber?: string;
  jobName?: string;
  clientName?: string;
  contractorName?: string;
  status?: string;
  dateFrom?: Date;
  dateTo?: Date;
  weekEnding?: string;
  useBtcPrefix?: boolean;
  useRelPrefix?: boolean;
}

export interface InvoiceStats {
  totalInvoices: number;
  draftInvoices: number;
  sentInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
  cancelledInvoices: number;
  totalAmount: number;
  totalPaid: number;
  totalOutstanding: number;
  averageInvoiceAmount: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
}

export interface InvoiceSortOptions {
  field: 'invoiceNumber' | 'jobName' | 'clientName' | 'invoiceDate' | 'dueDate' | 'totalAmount' | 'status';
  direction: 'asc' | 'desc';
}

export interface InvoiceGenerationOptions {
  timesheetIds: string[];
  jobId?: string;
  clientId?: string;
  weekEnding: string;
  invoiceDate: Date;
  useBtcPrefix?: boolean;
  useRelPrefix?: boolean;
  groupByEmployee?: boolean;
  includeExpenses?: boolean;
}

export interface InvoiceExportOptions {
  format: 'pdf' | 'excel' | 'csv';
  invoiceIds: string[];
  includeDetails?: boolean;
  groupBy?: 'client' | 'job' | 'date';
}

export interface InvoicePDFOptions {
  margin: number;
  filename: string;
  image: {
    type: 'jpeg' | 'png';
    quality: number;
  };
  html2canvas: {
    scale: number;
  };
  jsPDF: {
    unit: 'mm' | 'in' | 'pt';
    format: 'a4' | 'letter';
    orientation: 'portrait' | 'landscape';
  };
}

export interface BusinessInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  paymentTerms: string;
  logoUrl?: string;
}

export interface InvoiceValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface InvoiceValidationResult {
  isValid: boolean;
  errors: InvoiceValidationError[];
  warnings?: InvoiceValidationError[];
}

// Constants for invoice calculations and business rules
export const INVOICE_CONSTANTS = {
  DEFAULT_PAYMENT_TERMS: 30, // days
  DEFAULT_TAX_RATE: 0, // percentage
  OVERTIME_MULTIPLIER: 1.5,
  DOUBLE_TIME_MULTIPLIER: 2.0,
  CLIENTS: {
    CEC: {
      name: "CEC Facilities Group",
      address: "1275 Valley View Lane",
      city: "Irving",
      state: "TX",
      zip: "75061"
    },
    BTC: {
      name: "Build.Test.Connect Solutions LLC",
      address: "",
      city: "",
      state: "",
      zip: ""
    },
    RELIABLE: {
      name: "Reliable Electrical Services",
      address: "",
      city: "",
      state: "",
      zip: ""
    }
  },
  STATUSES: {
    DRAFT: 'draft',
    SENT: 'sent',
    PAID: 'paid',
    OVERDUE: 'overdue',
    CANCELLED: 'cancelled'
  },
  PREFIXES: {
    BTC: 'BTC-',
    REL: 'REL-'
  }
} as const;

// Type guards for runtime type checking
export const isInvoice = (obj: any): obj is Invoice => {
  return obj && 
    typeof obj.id === 'string' &&
    typeof obj.invoiceNumber === 'string' &&
    typeof obj.jobId === 'string' &&
    typeof obj.jobNumber === 'string' &&
    typeof obj.jobName === 'string' &&
    typeof obj.clientName === 'string' &&
    obj.invoiceDate instanceof Date &&
    obj.dueDate instanceof Date &&
    Array.isArray(obj.lineItems) &&
    typeof obj.totalAmount === 'number' &&
    ['draft', 'sent', 'paid', 'overdue', 'cancelled'].includes(obj.status);
};

export const isInvoiceLineItem = (obj: any): obj is InvoiceLineItem => {
  return obj &&
    typeof obj.id === 'string' &&
    typeof obj.employeeName === 'string' &&
    typeof obj.regularHours === 'number' &&
    typeof obj.overtimeHours === 'number' &&
    typeof obj.regularRate === 'number' &&
    typeof obj.overtimeRate === 'number' &&
    typeof obj.lineTotal === 'number' &&
    typeof obj.isExpense === 'boolean';
};

// Utility types for creating and updating invoices
export type CreateInvoiceData = Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateInvoiceData = Partial<Omit<Invoice, 'id' | 'createdAt' | 'createdBy'>>;
export type CreateJobData = Omit<Job, 'id' | 'createdAt' | 'updatedAt'>;
export type CreateClientData = Omit<Client, 'id' | 'createdAt' | 'updatedAt'>;
export type CreateActivityData = Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>;
